import SERVICES from "state/middleware/api/services";
import { makeToast } from "state/ui/toaster/ToasterActions";

const PDF_UPLOAD_TIMEOUT_MS = 300000; // 5 minutes

export const PDF_IMPORT_REQUEST = "PDF_IMPORT_REQUEST";
export const PDF_IMPORT_SUCCESS = "PDF_IMPORT_SUCCESS";
export const PDF_IMPORT_FAILURE = "PDF_IMPORT_FAILURE";
export const PDF_IMPORT_PROGRESS = "PDF_IMPORT_PROGRESS";
export const PDF_FILE_SELECT = "PDF_FILE_SELECT";
export const PDF_IMPORT_CLEAR_ERROR = "PDF_IMPORT_CLEAR_ERROR";
export const PDF_IMPORT_RETRY = "PDF_IMPORT_RETRY";
export const PDF_IMPORT_RESET = "PDF_IMPORT_RESET";

export const selectPdfFile = file => ({
  type: PDF_FILE_SELECT,
  payload: { file }
});

export const clearPdfImportError = () => ({
  type: PDF_IMPORT_CLEAR_ERROR
});

export const resetPdfImportState = () => ({
  type: PDF_IMPORT_RESET
});

export const updatePdfUploadProgress = progress => ({
  type: PDF_IMPORT_PROGRESS,
  payload: { progress }
});

export const uploadPdf = file => (dispatch, getState) => {
  const state = getState();
  const teamId = state.ui?.currentTeam?.id;
  const userId = state.currentUser?.id;
  const token = state.currentUser?.token;

  if (!teamId || !userId || !token) {
    dispatch({
      type: PDF_IMPORT_FAILURE,
      payload: { error: "User authentication required" }
    });
    return;
  }

  dispatch({ type: PDF_IMPORT_REQUEST });

  const formData = new FormData();
  formData.append("file", file);
  formData.append("userId", userId);
  formData.append("teamId", teamId);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", e => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 100);
        dispatch(updatePdfUploadProgress(progress));
      }
    });

    xhr.addEventListener("load", () => {
      console.log("PDF Upload Response:", {
        status: xhr.status,
        statusText: xhr.statusText,
        responseText: xhr.responseText,
        responseURL: xhr.responseURL
      });

      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const response = JSON.parse(xhr.responseText);
          dispatch({
            type: PDF_IMPORT_SUCCESS,
            payload: response
          });
          dispatch(
            makeToast({
              textComponent:
                "PDF imported successfully! Design saved to Workspace Drafts.",
              type: "noButton",
              icon: "success",
              duration: 5000,
              toastId: "pdf-import-success"
            })
          );
          resolve(response);
        } catch (error) {
          console.error(
            "Failed to parse PDF import response as JSON:",
            error,
            xhr.responseText
          );
          const errorMessage =
            "Failed to parse server response. Please try again.";
          dispatch({
            type: PDF_IMPORT_FAILURE,
            payload: { error: errorMessage }
          });
          dispatch(
            makeToast({
              textComponent:
                "PDF import failed: Unexpected server response. Please try again.",
              type: "noButton",
              icon: "warning",
              duration: 7000,
              toastId: "pdf-import-error"
            })
          );
          reject(error);
        }
      } else {
        let errorMessage = "Upload failed";
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          // Extract the most relevant error message
          errorMessage =
            errorResponse.message ||
            errorResponse.error?.message ||
            errorResponse.error?.name ||
            errorMessage;
        } catch (e) {
          // Provide user-friendly messages for common HTTP status codes
          console.log(
            "Failed to parse error response, using status code message for status:",
            xhr.status
          );
          switch (xhr.status) {
            case 404:
              errorMessage =
                "PDF import service not available. Please try again later or contact support.";
              break;
            case 401:
              errorMessage =
                "Authentication failed. Please refresh the page and try again.";
              break;
            case 403:
              errorMessage =
                "You do not have permission to import PDFs. Please contact your team administrator.";
              break;
            case 413:
              errorMessage =
                "PDF file is too large. Please try with a smaller file (max 50MB).";
              break;
            case 415:
              errorMessage =
                "File type not supported. Please select a valid PDF file.";
              break;
            case 429:
              errorMessage =
                "Too many upload attempts. Please wait a moment and try again.";
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              errorMessage =
                "Server error occurred. Please try again in a few minutes.";
              break;
            default:
              errorMessage = `Upload failed (Error ${xhr.status}). Please try again or contact support if the problem persists.`;
          }
        }
        dispatch({
          type: PDF_IMPORT_FAILURE,
          payload: { error: errorMessage }
        });
        dispatch(
          makeToast({
            textComponent: errorMessage,
            type: "noButton",
            icon: "warning",
            duration: 7000,
            toastId: "pdf-import-error"
          })
        );
        reject(new Error(errorMessage));
      }
    });

    xhr.addEventListener("error", () => {
      const errorMessage = "Network error occurred during upload";
      dispatch({
        type: PDF_IMPORT_FAILURE,
        payload: { error: errorMessage }
      });
      dispatch(
        makeToast({
          textComponent: errorMessage,
          type: "noButton",
          icon: "warning",
          duration: 7000,
          toastId: "pdf-import-error"
        })
      );
      reject(new Error(errorMessage));
    });

    xhr.addEventListener("timeout", () => {
      const errorMessage = "Upload timeout - please try again";
      dispatch({
        type: PDF_IMPORT_FAILURE,
        payload: { error: errorMessage }
      });
      dispatch(
        makeToast({
          textComponent: errorMessage,
          type: "noButton",
          icon: "warning",
          duration: 7000,
          toastId: "pdf-import-error"
        })
      );
      reject(new Error(errorMessage));
    });

    const url = `${SERVICES.RENDER}/teams/${teamId}/users/${userId}/imports`;

    // Debug logging
    console.log("PDF Upload Debug Info:");
    console.log("- URL:", url);
    console.log("- Team ID:", teamId);
    console.log("- User ID:", userId);
    console.log("- Token exists:", !!token);
    console.log("- File:", file.name, file.size, "bytes");

    xhr.open("POST", url, true);
    xhr.timeout = PDF_UPLOAD_TIMEOUT_MS;

    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("accept", "application/json");
    xhr.setRequestHeader("origin", window.location.origin);
    xhr.setRequestHeader("referer", window.location.href);

    xhr.send(formData);
  });
};

export const retryPdfUpload = () => (dispatch, getState) => {
  const state = getState();
  const selectedFile = state.ui?.pdfImport?.selectedFile;

  if (selectedFile) {
    dispatch(clearPdfImportError());
    dispatch(uploadPdf(selectedFile));
  }
};
